import { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen, Copy, Check } from 'lucide-react';
import { clsx } from 'clsx';

interface EntraSsoSetupGuideProps {
  cognitoEntityId: string;
  cognitoAcsUrl: string;
  signOnUrl: string;
  isProviderConfigured: boolean;
}

function CopyableField({
  label,
  value,
  fieldKey,
  copiedField,
  onCopy,
}: {
  label: string;
  value: string;
  fieldKey: string;
  copiedField: string | null;
  onCopy: (value: string, field: string) => void;
}) {
  return (
    <div className="rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
        {label}
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-sm font-mono text-gray-800 dark:text-gray-200 break-all">
          {value || 'Not available'}
        </code>
        {value && (
          <button
            onClick={() => onCopy(value, fieldKey)}
            className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-colors"
            title="Copy to clipboard"
          >
            {copiedField === fieldKey ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export function EntraSsoSetupGuide({
  cognitoEntityId,
  cognitoAcsUrl,
  signOnUrl,
  isProviderConfigured,
}: EntraSsoSetupGuideProps) {
  const [isExpanded, setIsExpanded] = useState(!isProviderConfigured);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const steps = [
    {
      number: 1,
      title: 'Create an Enterprise Application',
      content: (
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li>
            Go to <strong>Azure Portal</strong> &rarr; <strong>Microsoft Entra ID</strong> &rarr;{' '}
            <strong>Enterprise Applications</strong>
          </li>
          <li>
            Click <strong>&quot;New Application&quot;</strong> &rarr;{' '}
            <strong>&quot;Create your own application&quot;</strong>
          </li>
          <li>Name it (e.g., &quot;Connor Music Audit Portal&quot;)</li>
          <li>
            Select{' '}
            <strong>
              &quot;Integrate any other application not found in the gallery (Non-gallery)&quot;
            </strong>
          </li>
          <li>
            Click <strong>&quot;Create&quot;</strong>
          </li>
        </ol>
      ),
    },
    {
      number: 2,
      title: 'Configure SAML Single Sign-On',
      content: (
        <div className="space-y-3">
          <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
            <li>
              In the Enterprise App, go to <strong>&quot;Single sign-on&quot;</strong> &rarr; Select{' '}
              <strong>&quot;SAML&quot;</strong>
            </li>
            <li>
              In <strong>&quot;Basic SAML Configuration&quot;</strong>, set the following values:
            </li>
          </ol>
          <CopyableField
            label="Identifier (Entity ID)"
            value={cognitoEntityId}
            fieldKey="entityId"
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <CopyableField
            label="Reply URL (ACS URL)"
            value={cognitoAcsUrl}
            fieldKey="acsUrl"
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <CopyableField
            label="Sign on URL (enables IdP-initiated SSO from Entra My Apps)"
            value={signOnUrl}
            fieldKey="signOnUrl"
            copiedField={copiedField}
            onCopy={handleCopy}
          />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The <strong>Sign on URL</strong> allows users to launch the portal directly from
            the Entra My Apps menu. Click <strong>&quot;Save&quot;</strong> in the Basic SAML
            Configuration panel.
          </p>
        </div>
      ),
    },
    {
      number: 3,
      title: 'Get SAML Credentials',
      content: (
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li>
            Note your <strong>Tenant ID</strong> from Azure Portal &rarr; Microsoft Entra ID &rarr;
            Overview
          </li>
          <li>
            On the SAML configuration page, in <strong>Section 3 (SAML Certificates)</strong>, copy
            the <strong>&quot;App Federation Metadata URL&quot;</strong>
          </li>
        </ol>
      ),
    },
    {
      number: 4,
      title: 'Create an App Registration for Group Sync',
      content: (
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li>
            Go to <strong>Azure Portal</strong> &rarr; <strong>App Registrations</strong> &rarr;{' '}
            <strong>&quot;New Registration&quot;</strong>
          </li>
          <li>Name it (e.g., &quot;Connor Music Portal - API&quot;)</li>
          <li>
            Under <strong>&quot;API Permissions&quot;</strong>, add:
          </li>
          <li className="ml-4 list-disc">
            Microsoft Graph &rarr; Application &rarr; <code>Group.Read.All</code>
          </li>
          <li className="ml-4 list-disc">
            Microsoft Graph &rarr; Application &rarr; <code>User.Read.All</code>
          </li>
          <li>
            Click <strong>&quot;Grant admin consent&quot;</strong> for your organization
          </li>
          <li>
            Under <strong>&quot;Certificates &amp; secrets&quot;</strong>, create a new{' '}
            <strong>client secret</strong> and copy its value
          </li>
          <li>
            Note the <strong>Application (Client) ID</strong> from the App Registration overview
          </li>
        </ol>
      ),
    },
    {
      number: 5,
      title: 'Enter Credentials Below',
      content: (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Fill in the <strong>Tenant ID</strong>, <strong>Client ID</strong>,{' '}
          <strong>Client Secret</strong>, and <strong>SAML Metadata URL</strong> in the
          configuration form below. Click <strong>&quot;Save Configuration&quot;</strong>.
        </p>
      ),
    },
    {
      number: 6,
      title: 'Fetch Groups and Map Roles',
      content: (
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li>
            After saving, scroll down to the <strong>&quot;Group Role Mapping&quot;</strong> section
          </li>
          <li>
            Click <strong>&quot;Fetch Groups&quot;</strong> to load your Entra ID groups
          </li>
          <li>
            Check the <strong>Admin</strong> column for groups that should have admin access
          </li>
          <li>
            Check the <strong>Employee</strong> column for groups that should have employee access
          </li>
          <li>
            Click <strong>&quot;Save Role Mappings&quot;</strong>
          </li>
        </ol>
      ),
    },
    {
      number: 7,
      title: 'Activate SSO',
      content: (
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li>
            In the <strong>&quot;SSO Activation&quot;</strong> section, click{' '}
            <strong>&quot;Activate SSO&quot;</strong>
          </li>
          <li>Employees can now sign in with their Microsoft accounts</li>
          <li>The admin bootstrap login remains available as a fallback</li>
        </ol>
      ),
    },
  ];

  return (
    <div
      className={clsx(
        'mb-6 rounded-lg border',
        'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
      )}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
            Microsoft Entra ID Setup Guide
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {steps.map((step) => (
            <div key={step.number} className="flex gap-3">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mt-0.5">
                <span className="text-xs font-bold text-blue-700 dark:text-blue-300">
                  {step.number}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  {step.title}
                </p>
                {step.content}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
